/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: subscriptions-service.js
 *
 * Created by randysorensen on 5/24/17.
 */

'use strict';

angular
    .module('prism-ui.content')
    .service('SubscriptionsService', function (
        _,
        $q,
        $location,
        $routeParams,
        AcceptProfileResourceFactory,
        BreakPrePostRollResourceFactory,
        ContentResourceFactory,
        IntermediateTemplatesResourceFactory,
        LanguagesService,
        NetworkResourceFactory,
        PackageResourceFactory,
        PaginationService,
        PartnerResourceFactory,
        SubscriptionsResourceFactory
    ) {
        var self = this;

        self.contentObject = null;
        self.contentCategoryObject = null;

        var defaultQueryOptions = {
            fetchOffset: 0,
            fetchLimit: 25,
            searchString: '',
            keywords: '',
            includeEnabled: true,
            includeDisabled: false,
        };
        self.queryOptions = _.cloneDeep(defaultQueryOptions);
        self.resetQueryOptions = function () {
            self.queryOptions = _.cloneDeep(defaultQueryOptions);
        };

        var convertPropertyToString = function(newQueryOpts, parameterName) {
            var paramValue = _.get(newQueryOpts, parameterName);
            if (_.isArray(paramValue)) {
                paramValue = _.join(paramValue, '');
            }
            if (_.isString(paramValue) && _.size(paramValue) >= 1) {
                newQueryOpts[parameterName] = paramValue;
            } else {
                delete newQueryOpts[parameterName];
            }
        };

        var createSearchSubQueryOptions = function () {
            var newQueryOpts = angular.copy(self.queryOptions);
            var locationObj = $location.search();
            for (var queryOpt in locationObj) {
                if (locationObj.hasOwnProperty(queryOpt)) {
                    if (!Array.isArray(locationObj[queryOpt]) && queryOpt !== 'contentType') {
                        locationObj[queryOpt] = JSON.parse('[' + '"' + locationObj[queryOpt] + '"' + ']');
                    }
                    newQueryOpts[queryOpt] = locationObj[queryOpt];
                }
            }

            //
            // TODO: ABC-11697 - https://levelsbeyond.atlassian.net/browse/ABC-11697
            //       Figure out why this case occurs, and fix it at the source.
            //           Once fixed, remove the convertPropertyToString() function also.
            //       Explanation:
            //           The server expects searchString & keywords to be strings.
            //           However, the parameters sometimes change into an array.
            //
            convertPropertyToString(newQueryOpts, 'searchString');
            convertPropertyToString(newQueryOpts, 'keywords');

            self.queryOptions = newQueryOpts;
            self.queryOptions.includeEnabled = !!self.queryOptions.includeEnabled;
            self.queryOptions.includeDisabled = !!self.queryOptions.includeDisabled;
        };

        var deleteSubQueryOptions = function () {
            var newQueryOpts = angular.copy(self.queryOptions);
            var locationObj = $location.search();
            for (var queryOpt in locationObj) {
                if (locationObj.hasOwnProperty(queryOpt) && !defaultQueryOptions.hasOwnProperty(queryOpt)) {
                    delete newQueryOpts[queryOpt];
                }
            }
            self.queryOptions = newQueryOpts;
        };

        self.getPartnerCustomMetadataProperties = function (partnerId) {
            var partnerCustomMetadataProperties = {};

            // create a map of the properties with the property names
            // as map keys
            return PartnerResourceFactory
                .getMetadataProperties({id: partnerId})
                .$promise
                .then(function (metaPropsArray) {
                    partnerCustomMetadataProperties = _.keyBy(metaPropsArray, 'name');
                    return partnerCustomMetadataProperties;
                });
        };

        self.getContentCategorySubscriptionData = function (contentId, categoryId) {
            var contentPromise = ContentResourceFactory.get({id: contentId}).$promise;
            var contentCategoryPromise = ContentResourceFactory.getContentCategories({
                id: contentId,
                categoryId: categoryId
            }).$promise;

            return $q.all({
                content: contentPromise,
                contentCategory: contentCategoryPromise
            })
                .then(function (subscriptionData) {
                    return {
                        content: subscriptionData.content,
                        contentCategory: subscriptionData.contentCategory['0']
                    };
                });
        };

        self.getContentCategorySubscriptions = function (contentId, contentCategoryId) {
            //
            // we need these two objects:
            //
            //	content
            // 		- for the breadcrumb
            //
            //	contentCategory
            // 		- for the breadcrumb
            //		- for the "category ID" (NOT the "content category
            //		  ID", that's its own thing)
            //
            var contentPromise = ContentResourceFactory
                .get({id: contentId})
                .$promise;
            var contentCategoryPromise = ContentResourceFactory
                .getContentCategory({contentCategoryId: contentCategoryId})
                .$promise;

            return $q.all({
                content: contentPromise,
                contentCategory: contentCategoryPromise
            }).then(function (data) {
                self.contentObject = data.content;
                self.contentCategoryObject = data.contentCategory;
                createSearchSubQueryOptions();

                return SubscriptionsResourceFactory.getSubscriptionsByContentCategoryFiltered(
                    {
                        contentId: data.content.id,
                        categoryId: data.contentCategory.category.id
                    },
                    self.queryOptions,
                    function (subs, headersCB) {
                        PaginationService.parseResponseHeaders(headersCB());
                        return subs;
                    }).$promise;
            });
        };

        self.getPartnerSubscriptions = function (partnerId) {
            if ($location.search().hasOwnProperty('contentType')) {
                createSearchSubQueryOptions();
            } else {
                deleteSubQueryOptions();
                delete self.queryOptions.contentType;
            }

            return SubscriptionsResourceFactory
                .getSubscriptionsFiltered({
                        partnerId: partnerId
                    },
                    self.queryOptions,
                    function (subs, headersCB) {
                        PaginationService.parseResponseHeaders(headersCB());
                        return subs;
                    }).$promise;
        };

        self.createSubscription = function (subscription) {
            return SubscriptionsResourceFactory.createSubscription(
                {
                    partnerId: subscription.partner.id,
                },
                subscription
            ).$promise;
        };

        self.updateSubscription = function (subscription) {
            return SubscriptionsResourceFactory.saveSubscription(
                {
                    partnerId: subscription.partner.id,
                    subId: subscription.id,
                },
                subscription
            ).$promise;
        };

        self.updateSubscriptions = function (subscriptions) {
            return SubscriptionsResourceFactory.saveSubscriptions({}, subscriptions).$promise;
        };

        self.getPartnerSubscriptionFilterData = function (type) {
            var contentPromise = ContentResourceFactory.query({type: type, fetchLimit: -1}).$promise;
            var categoryTypesPromise = PackageResourceFactory.getCategories().$promise;
            var categoriesPromise = PackageResourceFactory.getCategories({view: 'EXTENDED'}).$promise;
            var networksPromise = NetworkResourceFactory.getNetworks({fetchLimit: -1}).$promise;
            return $q.all({
                contentList: contentPromise,
                categoryTypes: categoryTypesPromise,
                categories: categoriesPromise,
                networks: networksPromise
            });
        };

        self.getNetworkBreakPrePostRolls = function (network) {
            if (network) {
                return NetworkResourceFactory.getBranding({id: network.id, view: 'breakPrePostRolls'}).$promise;
            } else {
                return [];
            }
        };

        self.getContentBreakPrePostRolls = function (content) {
            if (content) {
                return ContentResourceFactory.getBranding({id: content.id, view: 'breakPrePostRolls'}).$promise;
            } else {
                return [];
            }
        };

        self.getSeasonsVolumesForContents = function (content) {
            var contentPromise = ContentResourceFactory.get({id: content.id}).$promise;
            return $q.all({
                content: contentPromise
            });
        };

        var isHomogeneousNetwork = function (contents) {
            // Iterate an array of content DTOs and return true if
            // they all share the same network.
            var networkIds = _.map(contents, 'network.id');
            var uniqueNetworkIds = _.uniq(networkIds);
            return uniqueNetworkIds.length === 1;
        };

        var getNetworkId = function (subscriptions) {
            // Grab all unique content IDs
            var contents = _.uniqBy(subscriptions, function (sub) {
                return sub.content.id;
            });
            var contentIds = _.map(contents, 'content.id');

            // Grab content DTOs for each unique ID
            var contentPromises = [];
            _.forEach(contentIds, function (contentId) {
                contentPromises.push(ContentResourceFactory.get({id: contentId, view: 'EXTENDED'}).$promise);
            });

            // Return promise that resolves with the network ID
            // shared by each content, or undefined if they do not
            // share the same network
            var networkIdPromise = $q.defer();
            $q.all(contentPromises).then(function (contents) {
                if (isHomogeneousNetwork(contents)) {
                    networkIdPromise.resolve(contents[0].network.id);
                } else {
                    networkIdPromise.resolve(undefined);
                }
            });
            return networkIdPromise;
        };

        var getNetworkBrandingData = function (contentPromise, subscriptions) {
            var networkPromise = $q.defer();

            contentPromise.then(function (content) {
                var networkIdPromise;

                // If the content promise is resolved with undefined,
                // it means there's more than one content among the
                // selected subscriptions. In this case, we need to
                // find whether these different contents belong to a
                // single network or not.
                if (content) {
                    networkIdPromise = $q.defer();
                    networkIdPromise.resolve(content.network.id);
                } else {
                    networkIdPromise = getNetworkId(subscriptions);
                }

                // If the network ID promise is resolved with
                // undefined, it means all selected subscriptions
                // belong to a single network, and we can obtain its
                // branding data.
                networkIdPromise.promise.then(function (networkId) {
                    if (networkId) {
                        NetworkResourceFactory.get({id: networkId, view: 'EXTENDED'}).$promise.then(function (network) {
                            networkPromise.resolve(network);
                        });
                    } else {
                        networkPromise.resolve(undefined);
                    }
                });
            });
            return networkPromise.promise;
        };

        self.getCollectionData = function (batchUpdate, isPartner, partner, content, category, subscriptions) {
            var promises = {};

            if (!batchUpdate || isPartner) {
                promises.partnerMetadata = self.getPartnerCustomMetadataProperties(partner.id);
                promises.partner = PartnerResourceFactory.get({id: partner.id, view: 'EXTENDED'}).$promise;
            }

            if (content) {
                promises.content = ContentResourceFactory.get({id: content.id, view: 'EXTENDED'}).$promise;
                if (category) {
                    promises.contentCategory = self.getContentCategorySubscriptionData(content.id, category.id);
                }
            } else {
                var contentPromise = $q.defer();
                promises.content = contentPromise.promise;
                contentPromise.resolve(undefined);
            }
            promises.network = getNetworkBrandingData(promises.content, subscriptions);
            promises.intermediateTemplateData = IntermediateTemplatesResourceFactory.query({}).$promise;
            promises.artworkAcceptProfiles = AcceptProfileResourceFactory.query({profileType: 'OTHER'}).$promise;
            promises.languages = LanguagesService.getAllLanguages().then((allAvailableLanguages) => {
                return _
                    .chain(allAvailableLanguages)
                    .orderBy('name')
                    .partition({name: 'English'})
                    .flatten()
                    .value();
            });

            return $q.all(promises).then((data) => {
                return $q.all({
                    networkBreakPrePostRolls: self.getNetworkBreakPrePostRolls(data.network),
                    contentBreakPrePostRolls: content && content.type === 'Ad/PreRoll' ? null : self.getContentBreakPrePostRolls(content)
                })
                    .then((networkContentData) => {
                        if (data.network) {
                            data.network.breakPrePostRolls = networkContentData.networkBreakPrePostRolls;
                        }
                        if (data.content) {
                            data.content.breakPrePostRolls = networkContentData.contentBreakPrePostRolls;
                        }
                        return data;
                    });
            });
        };
    });
